<script setup>
import { Badge } from '@drama/ui'
import { ChevronDown, ExternalLink } from '@lucide/vue'
import { useI18n } from 'vue-i18n'

import LongText from '../../../components/LongText.vue'
import { formatDateTime, formatDuration, formatInt } from '../llm-format.js'

defineProps({
  logs: { type: Object, required: true },
  compact: { type: Boolean, default: false },
  showEmbedding: { type: Boolean, default: true },
  showModelDecisions: { type: Boolean, default: true },
  showAgentCalls: { type: Boolean, default: true },
  expandModelDecisions: { type: Boolean, default: false },
})

const { t } = useI18n()

function statusVariant(status) {
  switch (status) {
    case 'ok':
      return 'success'
    case 'aborted':
      return 'warning'
    case 'error':
      return 'danger'
    default:
      return 'neutral'
  }
}

function optionalMetric(value) {
  return value === null ? '—' : formatInt(value)
}

function modelDecisionResponse(call) {
  return call.response === null ? '' : JSON.stringify(call.response, null, 2)
}
</script>

<template>
  <div class="match-logs" :class="{ 'is-compact': compact }">
    <section v-if="showEmbedding" class="match-logs__group">
      <header>
        <div>
          <h3>{{ t('visualGameAdmin.llm.videoMatch.logs.embeddingTitle') }}</h3>
          <p>{{ t('visualGameAdmin.llm.videoMatch.logs.embeddingDescription') }}</p>
        </div>
        <Badge variant="neutral" size="sm">{{ logs.embedding_calls.length }}</Badge>
      </header>

      <p v-if="logs.embedding_calls.length === 0" class="match-logs__empty">
        {{ t('visualGameAdmin.llm.videoMatch.logs.noEmbedding') }}
      </p>
      <article v-for="call in logs.embedding_calls" :key="call.id" class="match-logs__call">
        <div class="match-logs__call-head">
          <div>
            <strong>{{ call.provider }} · {{ call.model }}</strong>
            <code>{{ call.operation_id }}</code>
          </div>
          <Badge :variant="statusVariant(call.status)" size="sm">
            {{ t(`visualGameAdmin.llm.videoMatch.status.${call.status}`) }}
          </Badge>
        </div>
        <dl>
          <div><dt>{{ t('visualGameAdmin.llm.videoMatch.logs.callId') }}</dt><dd><code>{{ call.id }}</code></dd></div>
          <div><dt>{{ t('visualGameAdmin.llm.videoMatch.logs.time') }}</dt><dd>{{ formatDateTime(call.created_at) }}</dd></div>
          <div><dt>{{ t('visualGameAdmin.llm.videoMatch.logs.duration') }}</dt><dd>{{ formatDuration(call.duration_ms) }}</dd></div>
          <div><dt>{{ t('visualGameAdmin.llm.videoMatch.logs.dimensions') }}</dt><dd>{{ formatInt(call.dimensions) }}</dd></div>
          <div><dt>{{ t('visualGameAdmin.llm.videoMatch.logs.tokens') }}</dt><dd>{{ optionalMetric(call.total_tokens) }}</dd></div>
          <div><dt>{{ t('visualGameAdmin.llm.videoMatch.logs.inputHash') }}</dt><dd><code>{{ call.input_hash }}</code></dd></div>
        </dl>
        <p v-if="call.error_reason !== null" class="vga-error">{{ call.error_reason }}</p>
        <RouterLink
          class="match-logs__link"
          :to="{ name: 'llm-calls', query: { capability: 'embedding', request_id: call.id } }"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ t('visualGameAdmin.llm.videoMatch.logs.openFullCall') }}
          <ExternalLink :size="14" />
        </RouterLink>
      </article>
    </section>

    <section v-if="showModelDecisions" class="match-logs__group">
      <header>
        <div>
          <h3>{{ t('visualGameAdmin.llm.videoMatch.logs.modelDecisionTitle') }}</h3>
          <p>{{ t('visualGameAdmin.llm.videoMatch.logs.modelDecisionDescription') }}</p>
        </div>
        <Badge variant="neutral" size="sm">{{ logs.model_decision_calls.length }}</Badge>
      </header>

      <p v-if="logs.model_decision_calls.length === 0" class="match-logs__empty">
        {{ t('visualGameAdmin.llm.videoMatch.logs.noModelDecision') }}
      </p>
      <details
        v-for="call in logs.model_decision_calls"
        :key="call.request_id"
        class="match-logs__call"
        :open="expandModelDecisions"
      >
        <summary class="match-logs__call-head">
          <div>
            <strong>{{ call.provider }} · {{ call.model }}</strong>
            <code>{{ call.request_id }}</code>
          </div>
          <span class="match-logs__summary-actions">
            <Badge :variant="statusVariant(call.status)" size="sm">
              {{ t(`visualGameAdmin.llm.videoMatch.status.${call.status}`) }}
            </Badge>
            <ChevronDown :size="16" />
          </span>
        </summary>
        <div class="match-logs__call-body">
          <dl>
            <div><dt>{{ t('visualGameAdmin.llm.videoMatch.logs.operation') }}</dt><dd><code>{{ call.billing_operation_id }}</code></dd></div>
            <div><dt>{{ t('visualGameAdmin.llm.videoMatch.logs.time') }}</dt><dd>{{ formatDateTime(call.created_at) }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.llm.videoMatch.logs.duration') }}</dt><dd>{{ formatDuration(call.duration_ms) }}</dd></div>
          </dl>
          <p v-if="call.error_message !== null" class="vga-error">{{ call.error_message }}</p>
          <LongText
            :label="t('visualGameAdmin.llm.videoMatch.logs.request')"
            :text="JSON.stringify(call.request, null, 2)"
            :copy-text="JSON.stringify(call.request, null, 2)"
            :collapse-over="900"
          />
          <LongText
            v-if="call.response !== null"
            :label="t('visualGameAdmin.llm.videoMatch.logs.response')"
            :text="modelDecisionResponse(call)"
            :copy-text="modelDecisionResponse(call)"
            :collapse-over="600"
          />
          <RouterLink
            class="match-logs__link"
            :to="{ name: 'llm-calls', query: { capability: 'text', request_id: call.request_id } }"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ t('visualGameAdmin.llm.videoMatch.logs.openFullCall') }}
            <ExternalLink :size="14" />
          </RouterLink>
        </div>
      </details>
    </section>

    <section v-if="showAgentCalls" class="match-logs__group match-logs__group--context">
      <header>
        <div>
          <h3>{{ t('visualGameAdmin.llm.videoMatch.logs.agentTitle') }}</h3>
          <p>{{ t('visualGameAdmin.llm.videoMatch.logs.agentDescription') }}</p>
        </div>
        <Badge variant="neutral" size="sm">{{ logs.agent_calls.length }}</Badge>
      </header>
      <ul>
        <li v-for="call in logs.agent_calls" :key="call.id">
          <div>
            <span><strong>{{ call.agent_role }}</strong> · {{ call.model }}</span>
            <span>{{ call.conversation_key }} · {{ formatDateTime(call.created_at) }} · {{ formatDuration(call.duration_ms) }}</span>
          </div>
          <RouterLink
            v-if="call.llm_request_id !== null"
            class="match-logs__link"
            :to="{ name: 'llm-calls', query: { capability: 'text', request_id: call.llm_request_id } }"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ t('visualGameAdmin.llm.videoMatch.logs.openFullCall') }}
            <ExternalLink :size="14" />
          </RouterLink>
        </li>
      </ul>
    </section>
  </div>
</template>

<style scoped>
.match-logs {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--drama-space-lg);
}

.match-logs.is-compact {
  grid-template-columns: 1fr;
}

.match-logs.is-compact .match-logs__group--context {
  grid-column: auto;
}

.match-logs__group {
  display: grid;
  align-content: start;
  gap: var(--drama-space-md);
  min-width: 0;
}

.match-logs__group--context {
  grid-column: 1 / -1;
}

.match-logs__group > header,
.match-logs__call-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--drama-space-md);
}

.match-logs h3,
.match-logs__group > header p {
  margin: 0;
}

.match-logs__group > header p,
.match-logs__empty {
  margin-top: var(--drama-space-xs);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
}

.match-logs__call {
  display: grid;
  gap: var(--drama-space-md);
  padding: var(--drama-space-md);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface-alt);
}

details.match-logs__call > summary {
  cursor: pointer;
  list-style: none;
}

details.match-logs__call > summary::-webkit-details-marker {
  display: none;
}

.match-logs__call-head > div {
  display: grid;
  gap: var(--drama-space-xs);
  min-width: 0;
}

.match-logs__call code,
.match-logs dd code {
  font-family: var(--drama-font-family-mono);
  font-size: var(--drama-font-size-xs);
  overflow-wrap: anywhere;
}

.match-logs__summary-actions,
.match-logs__link {
  display: inline-flex;
  align-items: center;
  gap: var(--drama-space-xs);
}

details[open] .match-logs__summary-actions svg {
  transform: rotate(180deg);
}

.match-logs__call-body {
  display: grid;
  gap: var(--drama-space-lg);
  padding-top: var(--drama-space-md);
  border-top: 1px solid var(--drama-color-border);
}

.match-logs dl {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--drama-space-sm) var(--drama-space-lg);
  margin: 0;
}

.match-logs dl div {
  min-width: 0;
}

.match-logs dt {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.match-logs dd {
  margin: var(--drama-space-xs) 0 0;
  overflow-wrap: anywhere;
}

.match-logs__link {
  width: fit-content;
  color: var(--drama-color-primary);
  font-size: var(--drama-font-size-sm);
  text-decoration: none;
}

.match-logs__group--context ul {
  display: grid;
  gap: var(--drama-space-xs);
  margin: 0;
  padding: 0;
  list-style: none;
}

.match-logs__group--context li {
  display: flex;
  justify-content: space-between;
  gap: var(--drama-space-md);
  padding: var(--drama-space-sm) 0;
  border-bottom: 1px solid var(--drama-color-border);
  font-size: var(--drama-font-size-sm);
}

.match-logs__group--context li > div {
  display: grid;
  gap: var(--drama-space-xs);
}

.match-logs__group--context li > div span:last-child {
  color: var(--drama-color-text-muted);
}

@media (max-width: 860px) {
  .match-logs {
    grid-template-columns: 1fr;
  }

  .match-logs__group--context {
    grid-column: auto;
  }
}
</style>
